import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import {
  BaseDropdown,
  BASE_DROPDOWN_POSITION,
  Menu,
  MenuItem
} from "@widgets/inputs/dropdown/index.jsx";
import {
  Badge,
  BADGE_SHAPES,
  OverflowText
} from "@widgets/datadisplay/index.jsx";

import styles from "./tab.css";

import HorizontalModeIcon from "./HorizontalMore.svg";

export const BOX_TAB_VISIBLE_TYPES = {
  WITH_ONLY_ICONS: "onlyIcons",
  WITH_ONLY_TITLE: "onlyTitle",
  ICON_WITH_TITLE: "iconWithTitle"
};

BoxTabs.propTypes = {
  /** A list of SecondaryTab components. */
  children: PropTypes.node.isRequired,
  /** The id of the currently visible box tab. */
  activeTabId: PropTypes.string.isRequired,
  /** Size property is used to defined the size of the tab as medium and small. */
  size: PropTypes.string,
  /** A callback function that gets invoked when the current tab changes. */
  onChange: PropTypes.func.isRequired,
  /** Prevent the headers of the tabs from being clicked by disabling it.  */
  disabled: PropTypes.bool,
  /** Number of tab headers to show... The remaining will be moved into the dropdown. */
  maxVisible: PropTypes.number,
  /** A boolean value to show tabs width as fixed or adjsuted to its content */
  fixedWidth: PropTypes.bool,
  /** How will be tabs content ie., only contains icons | icons with text | only contaiins text */
  visibleType: PropTypes.string
};

BoxTabs.defaultProps = {
  maxVisible: 6,
  disabled: false,
  fixedWidth: false
};

const getTabItems = (children, maxVisible, activeTabId) => {
  const tabItems = React.Children.toArray(children).slice(0);
  const activeTabIndex = tabItems.findIndex(
    (tabItem) => tabItem.props.tabId === activeTabId
  );
  if (activeTabIndex >= maxVisible) {
    tabItems.splice(maxVisible - 1, 0, tabItems.splice(activeTabIndex, 1)[0]);
  }
  const visibleTabs = tabItems.slice(0, maxVisible);
  const hiddenTabs = tabItems.slice(maxVisible);
  return { visibleTabs, hiddenTabs };
};

const useEffectAfterInitialMount = (effect, deps) => {
  const isInitialRender = useRef(true);
  useEffect(function effectHandler() {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export function BoxTabs(props) {
  const {
    children,
    maxVisible,
    activeTabId,
    onChange,
    fixedWidth,
    visibleType,
    ...restProps
  } = props;
  const wrapperRef = useRef();
  const activeBoxRef = useRef(true);

  const [tabs, setTabs] = useState(
    getTabItems(children, maxVisible, activeTabId)
  );

  const { visibleTabs, hiddenTabs } = tabs;

  function onActiveTabChange(applyTransition = true) {
    setTimeout(() => {
      const activeTabElement = wrapperRef.current.querySelector(
        `[id="activeBoxTabItem-${activeTabId}"]`
      );
      if (!activeTabElement) return;
      const activeBox = activeBoxRef.current;
      if (!activeBox) return;

      requestAnimationFrame(() => {
        const isRTL = window.isRTL;
        const { offsetLeft, offsetWidth, parentElement } = activeTabElement;
        activeBox.style.width = `${offsetWidth + 3}px`;
        if (isRTL) {
          const translateX =
            parentElement.offsetWidth - (offsetLeft + offsetWidth);
          activeBox.style.transform = `translateX(${-translateX + 1}px)`;
        } else {
          activeBox.style.transform = `translateX(${offsetLeft - 1}px)`;
        }

        // setting visibility to visible to make the active box visible on mount,
        // initially it will be hidden to avoid jumping
        activeBox.style.visibility = "visible";

        // setting transition for the active box here as transition is not required on mount
        if (applyTransition) {
          activeBox.style.transition = "all 0.3s ease-in-out";
        }

        // setting border radius for the active tab
        const activeTabIndex = visibleTabs.findIndex(
          (tab) => tab.props.tabId === activeTabId
        );

        if (activeTabIndex < 1) {
          activeBox.style.borderRadius = isRTL ? "0 4px 4px 0" : "4px 0 0 4px";
        } else if (
          activeTabIndex === visibleTabs.length - 1 &&
          hiddenTabs.length === 0
        ) {
          activeBox.style.borderRadius = isRTL ? "4px 0 0 4px" : "0 4px 4px 0";
        } else {
          activeBox.style.borderRadius = "0";
        }
      }, 60);
    }, 60);
  }

  useLayoutEffect(
    function activeTabHandlerOnInit() {
      onActiveTabChange(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTabs.length]
  );

  useEffectAfterInitialMount(
    function activeTabHandlerOnChange() {
      onActiveTabChange();
    },
    [activeTabId]
  );

  useEffectAfterInitialMount(
    function onMaxVisibleChangeHandler() {
      setTabs(getTabItems(children, maxVisible, activeTabId));
      onActiveTabChange();
    },
    [maxVisible]
  );

  useEffectAfterInitialMount(
    function onChildrenChange() {
      setTabs(getTabItems(children, maxVisible, activeTabId));
      return;
    },
    [children]
  );

  useLayoutEffect(function highlightActiveTab() {
    const mutationObserver = new MutationObserver(() => {
      onActiveTabChange();
    });
    mutationObserver.observe(wrapperRef.current, {
      attributes: true,
      childList: true,
      subtree: true
    });
    onActiveTabChange();
    return () => mutationObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onMoreTabItemClick(item, index) {
    const [newVisible, newHidden] = [[...visibleTabs], [...hiddenTabs]];
    const lastVisibleItem = newVisible.pop();
    newVisible.push(item);
    newHidden[index] = lastVisibleItem;
    setTabs({ visibleTabs: newVisible, hiddenTabs: newHidden });
  }

  return (
    <div
      className={`${styles.boxtabs} ${fixedWidth && styles.gridAutoColumn}`}
      ref={wrapperRef}
      {...restProps}
    >
      <div className={styles.activeBox} ref={activeBoxRef} />
      {visibleTabs.map((child) => {
        const { title, icon, tabId, badgeArgs, disabled, ...restChildProps } =
          child.props;
        return (
          <button
            className={`${styles.boxtabitem} ${
              visibleType === BOX_TAB_VISIBLE_TYPES.WITH_ONLY_ICONS
                ? styles.icononly
                : ""
            } ${disabled && styles.disabled}`}
            key={tabId}
            id={activeTabId === tabId ? `activeBoxTabItem-${activeTabId}` : ""}
            onClick={() => {
              onChange && onChange(tabId);
            }}
            {...restChildProps}
          >
            {visibleType !== BOX_TAB_VISIBLE_TYPES.WITH_ONLY_TITLE && icon}
            {visibleType !== BOX_TAB_VISIBLE_TYPES.WITH_ONLY_ICONS && title && (
              <OverflowText className={`${styles.boxTabTitle} small`}>
                <span>{title}</span>
              </OverflowText>
            )}
            {badgeArgs && (
              <Badge shape={BADGE_SHAPES.BOX} maxCount={1000} {...badgeArgs} />
            )}
          </button>
        );
      })}
      {hiddenTabs.length > 0 && (
        <BaseDropdown
          targetRenderer={() => {
            return (
              <button
                className={`smallplus600 ${styles.boxtabitem} ${styles.icononly}`}
              >
                {<HorizontalModeIcon />}
              </button>
            );
          }}
          position={BASE_DROPDOWN_POSITION.BOTTOM_RIGHT}
          listRenderer={({ closePopup }) => {
            return (
              <Menu>
                {hiddenTabs.map((item, index) => {
                  const { tabId, icon, title, badgeArgs } = item.props;
                  return (
                    <MenuItem
                      key={tabId}
                      isSelected={activeTabId === tabId}
                      onClick={() => {
                        onChange(tabId);
                        closePopup();
                        onMoreTabItemClick(item, index);
                      }}
                    >
                      <span className={styles.hiddenMenus}>
                        {visibleType !==
                          BOX_TAB_VISIBLE_TYPES.WITH_ONLY_TITLE && icon}
                        {visibleType !==
                          BOX_TAB_VISIBLE_TYPES.WITH_ONLY_ICONS &&
                          title && (
                            <OverflowText
                              className={`${styles.titleContainer} small`}
                            >
                              <span>{title}</span>
                            </OverflowText>
                          )}
                        {badgeArgs && (
                          <Badge
                            shape={BADGE_SHAPES.BOX}
                            maxCount={1000}
                            {...badgeArgs}
                          />
                        )}
                      </span>
                    </MenuItem>
                  );
                })}
              </Menu>
            );
          }}
        />
      )}
    </div>
  );
}

BoxTab.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.node
};

export function BoxTab(props) {
  const { children } = props;
  return children;
}
